import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, X, User, FileText, Building2, GraduationCap, UserCheck, Users } from 'lucide-react';

const categoryIcons = {
  Stagiaires: User,
  Formateurs: GraduationCap,
  Tuteurs: UserCheck,
  Entreprises: Building2,
  Documents: FileText,
  Candidats: Users,
  Centres: Building2,
  Responsables: User,
  Moniteurs: User,
  Autres: User,
};

const categoryColors = {
  Stagiaires: 'text-blue-400',
  Formateurs: 'text-purple-400',
  Tuteurs: 'text-amber-400',
  Entreprises: 'text-emerald-400',
  Documents: 'text-rose-400',
  Candidats: 'text-cyan-400',
};

const GlobalSearch = ({ onNavigate }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/search/global?q=${encodeURIComponent(query)}`);
        setResults(res.data.results || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const grouped = results.reduce((acc, r) => {
    if (!acc[r.category]) acc[r.category] = [];
    acc[r.category].push(r);
    return acc;
  }, {});

  const handleSelect = (item) => {
    setQuery('');
    setOpen(false);
    setResults([]);
    navigate(item.link);
    if (onNavigate) onNavigate();
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className="relative mb-4 px-1" data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Rechercher..."
          className="w-full pl-9 pr-8 py-2 rounded-lg text-sm bg-slate-800 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#8DC63F] focus:border-[#8DC63F]"
          data-testid="global-search-input"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
            data-testid="global-search-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && (
        <div
          className="absolute left-0 right-0 mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto"
          data-testid="global-search-results"
        >
          {loading && (
            <div className="px-4 py-3 text-sm text-slate-400">Recherche...</div>
          )}

          {!loading && results.length === 0 && query.length >= 2 && (
            <div className="px-4 py-3 text-sm text-slate-400">Aucun résultat</div>
          )}

          {!loading && Object.keys(grouped).map((category) => {
            const Icon = categoryIcons[category] || User;
            const colorClass = categoryColors[category] || 'text-slate-400';
            return (
              <div key={category}>
                <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-800/80 sticky top-0 flex items-center gap-2">
                  <Icon className={`h-3.5 w-3.5 ${colorClass}`} />
                  {category}
                </div>
                {grouped[category].map((item) => (
                  <button
                    key={`${item.type}-${item.id}`}
                    onClick={() => handleSelect(item)}
                    className="w-full text-left px-4 py-2 hover:bg-slate-700 transition-colors flex items-start gap-3"
                    data-testid={`search-result-${item.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white truncate">{item.title}</p>
                      <p className="text-xs text-slate-400 truncate">{item.subtitle}</p>
                    </div>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
